import { useTranslation } from 'react-i18next';
import { LucideExternalLink } from 'lucide-react';
import type { BaseArticleDetail } from '~/common/types/baseArticleDetail';
import type { IArticleFactDocumentDto } from '~/dtos/hub/hub.models.generated';
import { useBasePath } from '~/common/hooks/useBasePath';
import { DownloadButton } from '~/common/components/DownloadButton';
import type { ReactElement } from 'react';
import { cn } from '~/common/utils/styleUtil';
import { getLinkRel } from '~/common/utils/urlUtil';

interface ProductInformationDownloadsAndLinksProps {
  article: BaseArticleDetail;
}

function KeyValueItem({ title, value, className }: Readonly<{ title: string; value: ReactElement; className?: string }>) {
  if (!value) return null;

  return (
    <div className={cn('flex justify-between min-h-[50px] items-center', className)}>
      <div>
        <h3 className={'text-base font-normal'}>{title}</h3>
      </div>
      <div className={'text-right'}>{value}</div>
    </div>
  );
}

export function ProductInformationDownloadsAndLinks({ article }: Readonly<ProductInformationDownloadsAndLinksProps>) {
  const { t } = useTranslation();
  const { lang } = useBasePath();

  if (!article?.articleNumber) {
    return null;
  }

  const url = `/${lang}/resources/articles/${article.articleNumber}/factsheet`;
  return (
    <div>
      <KeyValueItem
        title={t('Produktkurzspezifikation')}
        value={<DownloadButton name={`${article.articleNumber}.pdf`} url={url} />}
      />
      {article.ecoScoreDataSheetUrl && (
        <KeyValueItem
          title={t('ECO-SCORE® Details ansehen')}
          value={
            <a
              className={'flex items-center justify-center rounded h-[40px] w-[40px] hover:bg-accent'}
              target="_blank"
              href={article.ecoScoreDataSheetUrl}
              rel={getLinkRel(article.ecoScoreDataSheetUrl)}
            >
              <LucideExternalLink size={24} />
            </a>
          }
        />
      )}
      {article.factDocuments?.map((factDoc: IArticleFactDocumentDto, index: number) => {
        return (
          <KeyValueItem
            className={cn('border-t')}
            key={factDoc.id}
            title={factDoc.name ? factDoc.name : factDoc.docName}
            value={
              factDoc.url ? (
                <a
                  className={'flex items-center justify-center rounded h-[40px] w-[40px] hover:bg-accent'}
                  target={'_blank'}
                  href={factDoc.url}
                  rel={getLinkRel(factDoc.url)}
                >
                  <LucideExternalLink size={24} />
                </a>
              ) : (
                <DownloadButton
                  url={`/${lang}/resources/factDocument/${factDoc.id}`}
                  name={`${index} - ${factDoc.name ? factDoc.name : factDoc.docName}.pdf`}
                />
              )
            }
          />
        );
      })}
    </div>
  );
}
